// src/pages/Books.jsx
// Public-facing books store — reads from Firestore "books" collection,
// renders each title as a clean, neutral catalog card, and routes into
// the in-app checkout flow (BookPurchase -> Yoco) rather than an external
// payment link, so PDF and hard-copy purchases are handled as separate
// products with their own validation and delivery-detail collection.
//
// Note: "About this book" opens as a portal-rendered modal rather than
// an in-card flip, matching the approach already used elsewhere in the
// app (avoids the earlier 3D flip-card issues).

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import BookPurchase from "../components/BookPurchase";
import { fetchPublicContent } from "../data/publicContent";


function formatCurrency(val, currency = "ZAR") {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(val);
}

// A book can have a different price per format (PDF vs hard copy). Show
// "From <lowest price>" when both are on offer and differ, otherwise just
// show the one price that actually applies.
function getDisplayPrice(book) {
  const currency = book.currency || "ZAR";
  const pdfOn = book.digitalAvailable !== false && Boolean(book.pdfUrl);
  const hardCopyOn =
    book.physicalAvailable !== false &&
    !(Number.isInteger(book.physicalStock) && book.physicalStock <= 0);

  const prices = [];
  if (pdfOn && book.price != null) prices.push(Number(book.price));
  if (hardCopyOn) {
    const hcPrice = book.physicalPrice ?? book.price;
    if (hcPrice != null) prices.push(Number(hcPrice));
  }

  if (prices.length === 0) return null;
  const min = Math.min(...prices);
  const isRange = prices.length > 1 && new Set(prices).size > 1;
  return {
    label: isRange ? "From" : "Price",
    text: formatCurrency(min, currency),
  };
}

// Deterministic-ish "cover tone" so books without a coverUrl still look
// distinct from one another instead of sharing one flat placeholder.
const COVER_TONES = [
  ["#DCE3EA", "#C3CEDA"],
  ["#E2DEDA", "#CFC8C0"],
  ["#DDE7E3", "#C4D2CC"],
  ["#E6DEE8", "#D2C4D8"],
  ["#DCE9EC", "#C0D6DB"],
];
function toneFor(id) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return COVER_TONES[hash % COVER_TONES.length];
}

// ── About-this-book modal (portal) ──────────────────────────────────────────

const BookModal = ({ book, tone, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const [tone1, tone2] = tone;
  const displayPrice = getDisplayPrice(book);

  return createPortal(
    <div
      className="bk-modal-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bk-modal" role="dialog" aria-modal="true" aria-label={`About ${book.title}`}>
        <button className="bk-modal__close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div
          className="bk-modal__cover"
          style={
            !book.coverUrl
              ? { background: `linear-gradient(155deg, ${tone1}, ${tone2})` }
              : undefined
          }
        >
          {book.coverUrl ? (
            <img src={book.coverUrl} alt={`Cover of ${book.title}`} />
          ) : (
            <span className="bk-modal__cover-icon" aria-hidden="true">📖</span>
          )}
        </div>

        <div className="bk-modal__body">
          <div className="bk-modal__eyebrow">About this book</div>
          <h2 className="bk-modal__title">{book.title}</h2>
          {book.author && <div className="bk-modal__author">by {book.author}</div>}
          <div className="bk-modal__divider" aria-hidden="true" />
          <p className="bk-modal__desc">{book.description}</p>

          <div className="bk-modal__footer">
            {displayPrice && (
              <div className="bk-stat">
                <span className="bk-stat__label">{displayPrice.label}</span>
                <span className="bk-stat__value">{displayPrice.text}</span>
              </div>
            )}
            <BookPurchase book={book} />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Single book ────────────────────────────────────────────────────────────

const BookCard = ({ book, index }) => {
  const [showAbout, setShowAbout] = useState(false);
  const tone = toneFor(book.id);
  const [tone1, tone2] = tone;
  const displayPrice = getDisplayPrice(book);

  return (
    <div className="bk-shelf-slot" style={{ "--i": index }}>
      <div className="bk-book">
        <div
          className="bk-book__cover"
          style={
            !book.coverUrl
              ? { background: `linear-gradient(155deg, ${tone1}, ${tone2})` }
              : undefined
          }
        >
          {book.coverUrl ? (
            <img className="bk-book__art" src={book.coverUrl} alt={`Cover of ${book.title}`} />
          ) : (
            <div className="bk-book__art bk-book__art--placeholder" aria-hidden="true">
              <span>📖</span>
            </div>
          )}
        </div>

        <div className="bk-book__body">
          {book.available !== false && (
            <div className="bk-badge bk-badge--live">
              <span className="bk-badge__dot" aria-hidden="true" />
              Available
            </div>
          )}

          <div className="bk-book__title">{book.title}</div>
          {book.author && <div className="bk-book__author">by {book.author}</div>}

          <div className="bk-book__divider" aria-hidden="true" />

          {displayPrice && (
            <div className="bk-book__stats">
              <div className="bk-stat">
                <span className="bk-stat__label">{displayPrice.label}</span>
                <span className="bk-stat__value">{displayPrice.text}</span>
              </div>
            </div>
          )}

          <div className="bk-book__actions">
            {book.description && (
              <button
                className="bk-btn bk-btn--ghost"
                onClick={() => setShowAbout(true)}
                aria-label={`About ${book.title}`}
              >
                About
              </button>
            )}
            <BookPurchase book={book} />
          </div>
        </div>
      </div>

      {showAbout && (
        <BookModal book={book} tone={tone} onClose={() => setShowAbout(false)} />
      )}
    </div>
  );
};

// ── Loading skeleton (shaped like books, not a spinner) ─────────────────────

const BookSkeleton = ({ index }) => (
  <div className="bk-shelf-slot" style={{ "--i": index }}>
    <div className="bk-book bk-book--skeleton">
      <div className="bk-book__cover bk-book__cover--skeleton" />
      <div className="bk-book__body">
        <div className="bk-skel-line bk-skel-line--title" />
        <div className="bk-skel-line bk-skel-line--author" />
      </div>
    </div>
  </div>
);

// ── Main page ────────────────────────────────────────────────────────────────

const Books = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicContent("books")
      .then((docs) => {
        if (cancelled) return;
        docs.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setBooks(docs);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="bk-page">
      <div className="bk-container">
        <header className="bk-header">
          <div className="bk-header__eyebrow">Resources</div>
          <h1 className="bk-header__title">Discover your next chapter</h1>
          <p className="bk-header__sub">
            Tools for growth — curated reading to support your mindset journey.
          </p>
        </header>

        {loading && (
          <div className="bk-grid">
            {[0, 1, 2, 3].map((i) => (
              <BookSkeleton key={i} index={i} />
            ))}
          </div>
        )}

        {error && (
          <div className="bk-state bk-state--error">
            Could not load books right now. Please try again later.
          </div>
        )}

        {!loading && !error && books.length === 0 && (
          <div className="bk-state">
            <div className="bk-state__icon">📚</div>
            <p>Books coming soon. Check back shortly.</p>
          </div>
        )}

        {!loading && books.length > 0 && (
          <div className="bk-grid">
            {books.map((book, i) => (
              <BookCard key={book.id} book={book} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Books;